import { useInfiniteQuery } from '@tanstack/react-query'
import {
  Card,
  Empty,
  Flex,
  Form,
  FormListFieldData,
  InputNumber,
  Select,
  Spin,
  Table,
  TableColumnsType
} from 'antd'
import { FormInstance } from 'antd/lib'
import { useTheme } from 'antd-style'
import { useMemo, useState } from 'react'

import { useStocksServiceGetInternalInventoryListEndpointKey } from 'src/api/queries'
import { GetMedicineListResponse, StocksService } from 'src/api/requests'
import { DEFAULT_PER_PAGE, requiredRules } from 'src/app-constants'
import { EditableFormItem, SubmitButtons, Text } from 'src/components'
import {
  RECIPE_MEDICINE_FORM,
  RECIPES_CONSUMABLES_FIELDS
} from 'src/features/persons/utils/constants'
import { useDebounce } from 'src/hooks'
import { useLocales } from 'src/locales'

type Props = {
  fields: FormListFieldData[]
  form?: FormInstance
  isFinished?: boolean
  remove: (index: number) => void
  loading?: boolean
}

const generateFields = (medicines: { name?: string }[]) =>
  medicines
    .map((_, index: number) =>
      RECIPES_CONSUMABLES_FIELDS.map((field) => [
        RECIPE_MEDICINE_FORM,
        index,
        field
      ])
    )
    .flat()

export const TestConsumablesTables = ({
  fields,
  form,
  remove,
  isFinished,
  loading
}: Props) => {
  const [search, setSearch] = useState('')

  const [editingIndex, setEditingIndex] = useState<number>()
  const [isNewConsumable, setIsNewConsumable] = useState(false)

  const debouncedSearch = useDebounce(search)
  const { t } = useLocales()

  const palette = useTheme()

  const treatmentMedicine = form?.getFieldValue('medicine')

  const medicamentsList = treatmentMedicine?.map(
    (item: { internationalNameId: string }) => item?.internationalNameId
  )

  const { data, isFetching, fetchNextPage } = useInfiniteQuery({
    queryKey: [
      useStocksServiceGetInternalInventoryListEndpointKey,
      debouncedSearch
    ],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const response = await StocksService.getInternalInventoryListEndpoint(
        pageParam,
        DEFAULT_PER_PAGE,
        debouncedSearch,
        undefined,
        undefined,
        undefined,
        undefined,
        medicamentsList
      )

      return {
        data: response.data,
        total_pages: response.totalPages ?? 0,
        current_page: pageParam
      }
    },
    getNextPageParam: (lastPage: {
      data?: GetMedicineListResponse[]
      total_pages: number
      current_page: number
    }) =>
      lastPage?.current_page < lastPage?.total_pages
        ? lastPage.current_page + 1
        : undefined,
    initialPageParam: 1
  })

  const medicines = data?.pages.flatMap((page) => page.data) || []

  const selectOptions = medicines.map((item) => ({
    label: item?.internationalName?.name,
    value: item?.id,
    item
  }))

  const list = Form.useWatch(RECIPE_MEDICINE_FORM, form) || []

  const filteredCombinedData = useMemo(() => {
    const selectedIds = new Set(list.map((item: { id: string }) => item.id))

    return selectOptions.filter((data) => !selectedIds.has(data.value))
  }, [selectOptions, list])

  const onCancel = (index: number) => {
    if (isNewConsumable) {
      remove(index)
    } else {
      form?.resetFields(
        RECIPES_CONSUMABLES_FIELDS.map((field) => [
          RECIPE_MEDICINE_FORM,
          index,
          field
        ])
      )
    }

    setIsNewConsumable(false)
    setEditingIndex(undefined)
  }

  const onSelect = async (value: string) => {
    const medicines = form?.getFieldValue(RECIPE_MEDICINE_FORM)

    if (medicines?.length > 0) {
      const fieldsToValidate = generateFields(medicines)

      await form?.validateFields(fieldsToValidate)
    }

    const selectedItem = selectOptions?.find((item) => item.value === value)

    if (selectedItem) {
      const newInquiryItem = {
        name: selectedItem?.label,
        internationalNameId: selectedItem?.item?.internationalName?.id,
        productFormId: selectedItem?.item?.productForm?.id,
        productFormName: selectedItem?.item?.productForm?.name,
        internalInventoryItemId: selectedItem.value,
        stockQuantity: selectedItem?.item?.currentQuantity
      }

      form?.setFieldsValue({
        [RECIPE_MEDICINE_FORM]: [...list, newInquiryItem]
      })
      setEditingIndex(list.length)
      setIsNewConsumable(true)
      setSearch('')
    }
  }

  const onSave = async () => {
    const medicines = form?.getFieldValue(RECIPE_MEDICINE_FORM)

    const fieldsToValidate = generateFields(medicines)

    const values = await form?.validateFields(fieldsToValidate)

    if (values) {
      setIsNewConsumable(false)
      setEditingIndex(undefined)
      form?.setFieldsValue({ [RECIPE_MEDICINE_FORM]: values?.recipesMedicine })
    }
  }

  const columns: TableColumnsType = useMemo(
    () => [
      {
        key: 'name',
        title: t('consultations.preparation_name'),
        render: (_, __, index) => {
          const um = form?.getFieldValue([RECIPE_MEDICINE_FORM, index, 'name'])

          return <Text>{um}</Text>
        }
      },

      {
        key: 'um',
        title: t('common.unit'),
        render: (_, __, index) => {
          const um = form?.getFieldValue([
            RECIPE_MEDICINE_FORM,
            index,
            'productFormName'
          ])

          return <Text>{um}</Text>
        }
      },
      {
        key: 'dispensedAmount',
        minWidth: 200,
        title: t('common.quantity'),
        render: (_, __, index) => {
          return (
            <EditableFormItem
              name={[index, 'dispensedAmount']}
              isEditing={editingIndex === index}
              rules={requiredRules}
            >
              <InputNumber
                min={1}
                placeholder={t('common.quantity')}
                className="full-width"
              />
            </EditableFormItem>
          )
        }
      },
      {
        key: 'stock quantity',
        title: t('investigations.stock_quantity'),
        render: (_, __, index) => {
          const um = form?.getFieldValue([
            RECIPE_MEDICINE_FORM,
            index,
            'stockQuantity'
          ])

          return <Text>{um}</Text>
        }
      },
      {
        width: 105,
        fixed: 'right' as const,
        key: 'actions',
        render: (_: unknown, __: unknown, index: number) => (
          <SubmitButtons
            editingIndex={editingIndex}
            isEditing={editingIndex === index}
            onSave={onSave}
            onCancel={() => onCancel(index)}
            onEdit={() => setEditingIndex(index)}
            onDelete={() => remove(index)}
          />
        )
      }
    ],
    [t, palette]
  )

  return (
    <>
      <Table
        dataSource={fields}
        rowKey={(record) => JSON.stringify(record)}
        scroll={{ x: 'max-content' }}
        columns={columns}
        pagination={false}
        loading={loading}
      />

      {!isFinished && (
        <Card bordered={false}>
          <Select
            allowClear
            showSearch
            className="full-width"
            value={null}
            onSearch={(value) => setSearch(value)}
            onClear={() => setSearch('')}
            loading={isFetching}
            options={filteredCombinedData}
            filterOption={false}
            onSelect={(value) => value && onSelect(value)}
            placeholder={t('common.search')}
            onPopupScroll={(e) => {
              const target = e.target as HTMLElement
              if (
                target.scrollTop + target.offsetHeight ===
                target.scrollHeight
              ) {
                fetchNextPage()
              }
            }}
            notFoundContent={
              isFetching ? (
                <Flex justify="center">
                  <Spin spinning />
                </Flex>
              ) : (
                <Empty />
              )
            }
          />
        </Card>
      )}
    </>
  )
}
